/**
 * How many accounts wait for approval: one shared count for the badges in the
 * rail and the phone header. It is read again every few minutes, when the tab
 * comes back, and right after an admin changes a role.
 */

/** Read again after this long. */
export const POLL_MS = 5 * 60 * 1000;
/** Mounting both badges, or switching tabs quickly, asks only once. */
export const MIN_GAP_MS = 30 * 1000;

interface Config {
  /** The number of accounts waiting (GET /v1/admin/users/pending). */
  load: () => Promise<number>;
  now?: () => number;
}

export function createPendingApprovals({ load, now = () => Date.now() }: Config) {
  let count = 0;
  let lastAsked = Number.NEGATIVE_INFINITY;
  let running: Promise<void> | null = null;
  const listeners = new Set<() => void>();

  const set = (next: number) => {
    if (next === count) return;
    count = next;
    listeners.forEach((listener) => listener());
  };

  /** force: right after an admin action; otherwise at most once per MIN_GAP_MS. */
  function refresh({ force = false } = {}): Promise<void> {
    if (running) return running;
    if (!force && now() - lastAsked < MIN_GAP_MS) return Promise.resolve();
    lastAsked = now();
    running = load()
      .then(set)
      // A failed read keeps the last count; the next poll tries again.
      .catch(() => {})
      .finally(() => {
        running = null;
      });
    return running;
  }

  return {
    refresh,
    getCount: () => count,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    /** Signed out: no badge for the next user until they are read. */
    reset() {
      lastAsked = Number.NEGATIVE_INFINITY;
      set(0);
    },
  };
}
